import React, { useEffect, useState } from "react";
import { Badge, Box, Text, Tooltip } from "@chakra-ui/react";
import { NUMBER_STATUSES, loadNumberStatus, onNumberStatusChange } from "../../utils/numberStatus";

// "Good number" / "Bad number" / ... badge for a phone number, from
// public.sms_number_health. Renders nothing while loading or when the status
// cannot be read, so it is safe to drop next to any phone number. `compact`
// uses the short label ("Good", "Bad") for narrow table cells; `letter` uses a
// single letter ("G", "B", "U") — the tooltip still gives the full text.
const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
};

export default function PhoneStatusBadge({ phone, compact = false, letter = false, ...badgeProps }) {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let live = true;
    setInfo(null);
    loadNumberStatus(phone).then((res) => {
      if (live) setInfo(res);
    });
    // Re-read after an SMS send; keep the current badge if the re-read fails.
    const unsubscribe = onNumberStatusChange(() => {
      loadNumberStatus(phone).then((res) => {
        if (live && res.status) setInfo(res);
      });
    });
    return () => {
      live = false;
      unsubscribe();
    };
  }, [phone]);

  const meta = info?.status && NUMBER_STATUSES[info.status];
  if (!meta) return null;

  const rows = info.rows || [];
  const delivered = rows.reduce((n, r) => n + (r.delivered_count || 0), 0);
  const failed = rows.reduce((n, r) => n + (r.failed_count || 0), 0);
  const lastError = rows
    .filter((r) => r.last_error_code || r.error_description)
    .sort((a, b) => new Date(b.last_failure_at || 0) - new Date(a.last_failure_at || 0))[0];
  const lastSuccess = formatDate(
    rows.map((r) => r.last_success_at).filter(Boolean).sort().pop(),
  );
  const lastFailure = formatDate(lastError?.last_failure_at);

  const tooltip = (
    <Box>
      <Text fontWeight="semibold">{meta.label}</Text>
      <Text fontSize="xs">{meta.hint}</Text>
      {rows.length > 0 && (
        <Text fontSize="xs" mt={1}>
          {delivered} delivered · {failed} failed
          {lastSuccess ? ` · last delivered ${lastSuccess}` : ""}
        </Text>
      )}
      {lastError && info.status !== "good" && (
        <Text fontSize="xs" mt={1}>
          Last error{lastError.last_error_code ? ` ${lastError.last_error_code}` : ""}
          {lastError.error_description ? `: ${lastError.error_description}` : ""}
          {lastFailure ? ` (${lastFailure})` : ""}
        </Text>
      )}
    </Box>
  );

  return (
    <Tooltip label={tooltip} hasArrow placement="bottom">
      <Badge
        colorScheme={meta.colorScheme}
        variant="solid"
        cursor="help"
        fontSize={compact || letter ? "2xs" : undefined}
        {...badgeProps}
      >
        {letter ? meta.letter : compact ? meta.short : meta.label}
      </Badge>
    </Tooltip>
  );
}
